import { useCallback, useEffect, useState } from 'react'

// beforeinstallprompt не типизирован в lib.dom — минимальный тип.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'pwa-install-dismissed'

const isStandalone = () => {
  if (typeof window === 'undefined') return false
  const displayMode =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches
  // iOS Safari
  const iosStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  return Boolean(displayMode) || iosStandalone
}

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !/crios|fxios/i.test(window.navigator.userAgent) // только Safari умеет «На экран Домой»

type InstallState = {
  /** Android/Chromium: доступен нативный install-prompt */
  canInstall: boolean
  /** iOS Safari: нативного prompt нет, показываем инструкцию */
  iosHint: boolean
  /** Уже установлено/запущено как standalone — ничего не показываем */
  installed: boolean
  promptInstall: () => Promise<void>
  dismiss: () => void
}

/**
 * Логика подсказки «Установить приложение».
 * - Android/Chromium: ловим beforeinstallprompt, показываем кнопку → prompt().
 * - iOS Safari: нативного события нет — показываем текстовую инструкцию.
 * - Скрываем, если уже standalone или пользователь закрыл подсказку.
 */
export const usePwaInstall = (): InstallState => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(() =>
    typeof window === 'undefined' ? false : isStandalone(),
  )
  const [dismissed, setDismissed] = useState(() =>
    typeof window === 'undefined' ? false : localStorage.getItem(DISMISS_KEY) === '1',
  )

  useEffect(() => {
    const onPrompt = (event: Event) => {
      event.preventDefault() // не показывать дефолтную мини-инфопанель Chrome
      setDeferred(event as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferred(null)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return
    await deferred.prompt()
    const choice = await deferred.userChoice
    if (choice.outcome === 'accepted') setInstalled(true)
    setDeferred(null) // событие одноразовое
  }, [deferred])

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1')
    setDismissed(true)
  }, [])

  const hidden = installed || dismissed
  const iosHint = !hidden && isIos() && !isStandalone()

  return {
    canInstall: !hidden && deferred !== null,
    iosHint,
    installed,
    promptInstall,
    dismiss,
  }
}
